import clsx from 'clsx'
import { forwardRef, HTMLProps } from 'react'
import sanitizeHtml from 'sanitize-html'

export interface SanitisedHTMLContainerProps extends HTMLProps<HTMLDivElement> {
	className?: string
	children: string
	allowedTags?: string[]
	allowedAttributes?: false | Record<string, sanitizeHtml.AllowedAttribute[]>
}

const SanitisedHTMLContainer = (
	{
		children,
		className,
		allowedTags = sanitizeHtml.defaults.allowedTags,
		allowedAttributes = sanitizeHtml.defaults.allowedAttributes,
		...others
	}: SanitisedHTMLContainerProps,
	ref,
) => {
	const sanitisedChildren = sanitizeHtml(children, {
		allowedTags,
		allowedAttributes,
		transformTags: {
			a: function (_tagName, attribs) {
				// Add target="_blank" to external links consistently
				if (attribs.href) {
					const href = attribs.href.trim()

					// Skip internal links, anchors, and relative paths
					if (
						href.startsWith('/') ||
						href.startsWith('#') ||
						href.startsWith('mailto:') ||
						href.startsWith('tel:')
					) {
						return { tagName: 'a', attribs }
					}

					// Check if it's an external link (has protocol and different domain)
					if (href.includes('://')) {
						try {
							const linkUrl = new URL(href)
							const currentHost =
								typeof window !== 'undefined'
									? window.location.hostname
									: ''

							// If hostname is different from current site, it's external
							if (linkUrl.hostname !== currentHost) {
								return {
									tagName: 'a',
									attribs: {
										...attribs,
										target: '_blank',
										rel: 'noopener noreferrer',
									},
								}
							}
						} catch (e) {
							// If URL parsing fails, treat as external for safety
							return {
								tagName: 'a',
								attribs: {
									...attribs,
									target: '_blank',
									rel: 'noopener noreferrer',
								},
							}
						}
					}
				}

				return { tagName: 'a', attribs }
			},
		},
	})

	return (
		// eslint-disable-next-line react/no-danger
		<div
			ref={ref}
			{...others}
			className={clsx('richtext', className)}
			dangerouslySetInnerHTML={{ __html: sanitisedChildren }}
		/>
	)
}

export default forwardRef(SanitisedHTMLContainer)
